import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, DollarSign, TrendingUp, Sparkles, Trash2, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import Logo from "@/components/Logo";

const STATUSES = ["new", "contacted", "converted", "closed"];

function StatCard({ testId, label, value, icon: Icon }) {
    return (
        <div data-testid={testId} className="bg-[#141414] border border-white/10 p-6">
            <div className="flex items-center justify-between">
                <Icon className="w-5 h-5 text-brand" />
                <div className="text-xs font-mono uppercase tracking-widest text-white/40">{label}</div>
            </div>
            <div className="mt-6 numeric-display font-semibold text-4xl text-white">{value}</div>
        </div>
    );
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("leads");
    const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, converted_leads: 0, total_revenue: 0, paid_transactions: 0 });
    const [leads, setLeads] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!localStorage.getItem("gwb_admin_token")) {
            navigate("/admin");
            return;
        }
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [statsR, leadsR, payR] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/leads"),
                api.get("/admin/payments"),
            ]);
            setStats(statsR.data);
            setLeads(leadsR.data.leads);
            setPayments(payR.data.payments);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("gwb_admin_token");
                navigate("/admin");
                return;
            }
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/leads/${id}`, { status });
            setLeads((cur) => cur.map((l) => (l.id === id ? { ...l, status } : l)));
            toast.success("Status updated");
            const s = await api.get("/admin/stats");
            setStats(s.data);
        } catch {
            toast.error("Update failed");
        }
    };

    const deleteLead = async (id) => {
        if (!window.confirm("Delete this lead permanently?")) return;
        try {
            await api.delete(`/admin/leads/${id}`);
            setLeads((cur) => cur.filter((l) => l.id !== id));
            toast.success("Lead deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const logout = () => {
        localStorage.removeItem("gwb_admin_token");
        localStorage.removeItem("gwb_admin_email");
        navigate("/admin");
    };

    const reconcileStripe = async () => {
        try {
            const { data } = await api.post("/admin/reconcile-stripe");
            toast.success(`Synced: ${data.added} added, ${data.updated} updated`);
            loadAll();
        } catch {
            toast.error("Sync failed");
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A]">
            {/* Header */}
            <header className="border-b border-white/10 bg-[#0A0A0A]">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Logo />
                        <span className="text-xs font-mono uppercase tracking-widest text-white/40 hidden sm:inline">Admin Console</span>
                    </div>
                    <button
                        data-testid={ADMIN.logoutBtn}
                        onClick={logout}
                        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-brand transition-colors"
                    >
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="eyebrow mb-2">Dashboard</div>
                        <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-[-0.04em] text-white">Overview</h1>
                    </div>
                    <button onClick={loadAll} className="text-xs font-mono uppercase tracking-widest text-white/50 hover:text-brand transition-colors">
                        {loading ? "Refreshing..." : "Refresh ↻"}
                    </button>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    <StatCard testId={ADMIN.statsLeads} label="Total leads" value={stats.total_leads} icon={Users} />
                    <StatCard testId={ADMIN.statsNew} label="New leads" value={stats.new_leads} icon={Sparkles} />
                    <StatCard testId={ADMIN.statsPayments} label="Paid orders" value={stats.paid_transactions} icon={TrendingUp} />
                    <StatCard testId={ADMIN.statsRevenue} label="Revenue (USD)" value={`$${stats.total_revenue.toLocaleString()}`} icon={DollarSign} />
                </div>

                <div className="flex items-center gap-1 border-b border-white/10 mb-6">
                    <button
                        data-testid={ADMIN.tabLeads}
                        onClick={() => setTab("leads")}
                        className={`px-5 py-3 text-sm font-semibold transition-colors border-b-2 ${tab === "leads" ? "border-brand text-white" : "border-transparent text-white/50 hover:text-white"}`}
                    >
                        Leads ({leads.length})
                    </button>
                    <button
                        data-testid={ADMIN.tabPayments}
                        onClick={() => setTab("payments")}
                        className={`px-5 py-3 text-sm font-semibold transition-colors border-b-2 ${tab === "payments" ? "border-brand text-white" : "border-transparent text-white/50 hover:text-white"}`}
                    >
                        Payments ({payments.length})
                    </button>
                </div>

                {tab === "leads" && (
                    <div className="bg-[#141414] border border-white/10 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left border-b border-white/10">
                                <tr className="text-xs font-mono uppercase tracking-widest text-white/40">
                                    <th className="py-4 px-4">Date</th>
                                    <th className="py-4 px-4">Name</th>
                                    <th className="py-4 px-4">Email</th>
                                    <th className="py-4 px-4">Phone</th>
                                    <th className="py-4 px-4">Interest</th>
                                    <th className="py-4 px-4">Message</th>
                                    <th className="py-4 px-4">Status</th>
                                    <th className="py-4 px-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.length === 0 && (
                                    <tr><td colSpan={8} className="py-16 text-center text-white/40">No leads yet.</td></tr>
                                )}
                                {leads.map((l) => (
                                    <tr key={l.id} data-testid={ADMIN.leadRow(l.id)} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                                        <td className="py-4 px-4 text-white/60 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()} <br /><span className="text-[10px] text-white/30">{new Date(l.created_at).toLocaleTimeString()}</span></td>
                                        <td className="py-4 px-4 text-white font-medium">{l.name}</td>
                                        <td className="py-4 px-4"><a href={`mailto:${l.email}`} className="text-brand hover:underline">{l.email}</a></td>
                                        <td className="py-4 px-4 text-white/70">{l.phone ? <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-brand">{l.phone}</a> : "—"}</td>
                                        <td className="py-4 px-4 text-white/70">{l.service_interest || "—"}</td>
                                        <td className="py-4 px-4 text-white/60 max-w-xs truncate" title={l.message}>{l.message}</td>
                                        <td className="py-4 px-4">
                                            <select
                                                data-testid={ADMIN.leadStatus(l.id)}
                                                value={l.status}
                                                onChange={(e) => updateStatus(l.id, e.target.value)}
                                                className="bg-transparent border border-white/20 text-white text-xs px-2 py-1 focus:outline-none focus:border-brand"
                                            >
                                                {STATUSES.map((s) => (<option key={s} value={s} className="bg-[#141414]">{s}</option>))}
                                            </select>
                                        </td>
                                        <td className="py-4 px-4">
                                            <button
                                                data-testid={ADMIN.leadDelete(l.id)}
                                                onClick={() => deleteLead(l.id)}
                                                className="text-white/40 hover:text-red-400 transition-colors"
                                                aria-label="Delete lead"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "payments" && (
                    <>
                        <div className="mb-4 flex justify-end">
                            <button
                                onClick={reconcileStripe}
                                data-testid="reconcile-stripe-btn"
                                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/70 hover:text-brand transition-colors border border-white/15 hover:border-brand px-4 py-2"
                            >
                                <RefreshCcw className="w-3.5 h-3.5" /> Sync with Stripe
                            </button>
                        </div>
                        <div className="bg-[#141414] border border-white/10 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left border-b border-white/10">
                                <tr className="text-xs font-mono uppercase tracking-widest text-white/40">
                                    <th className="py-4 px-4">Date</th>
                                    <th className="py-4 px-4">Customer</th>
                                    <th className="py-4 px-4">Package</th>
                                    <th className="py-4 px-4">Amount</th>
                                    <th className="py-4 px-4">Status</th>
                                    <th className="py-4 px-4">Session</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.length === 0 && (
                                    <tr><td colSpan={6} className="py-16 text-center text-white/40">No payments yet.</td></tr>
                                )}
                                {payments.map((p) => (
                                    <tr key={p.session_id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                                        <td className="py-4 px-4 text-white/60 whitespace-nowrap">{new Date(p.created_at).toLocaleString()}</td>
                                        <td className="py-4 px-4 text-white">{p.customer_email || p.customer_name || "—"}</td>
                                        <td className="py-4 px-4 text-white/70">{p.lookup_key}</td>
                                        <td className="py-4 px-4 text-white font-mono">{((p.amount_total || p.amount || 0) / 100).toFixed(2)} {(p.currency || "").toUpperCase()}</td>
                                        <td className="py-4 px-4">
                                            <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${
                                                p.payment_status === "paid" ? "bg-brand/20 text-brand" :
                                                p.payment_status === "pending" ? "bg-yellow-400/10 text-yellow-400" :
                                                "bg-red-400/10 text-red-400"
                                            }`}>{p.payment_status}</span>
                                        </td>
                                        <td className="py-4 px-4 font-mono text-[10px] text-white/40">{p.session_id.slice(0, 20)}...</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
